/**
 * iter 34 Phase D follow-up — Settings → Email Templates editor.
 *
 * Admins can create/update an org override for each `doc_type` template.
 * System defaults are shown but read-only — saving creates a per-org
 * override that supersedes the default on resolve. Deleting an override
 * reverts to the system default.
 *
 *   GET  /api/email-templates           list system + org templates
 *   POST /api/email-templates           upserts an org override by doc_type
 *   DELETE /api/email-templates/{id}    removes an org override
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Drawer } from "./UI";
import { PencilSimple, Trash, EnvelopeSimple, ArrowCounterClockwise } from "@phosphor-icons/react";

const KNOWN_VARS = [
  "customer_name", "attorney_name", "server_name",
  "job_number", "case_number", "billing_ref", "account_number",
  "document_title", "document_url",
  "sender_name", "special_instructions",
  // iter 56 — collaboration_invite vars
  "sender_org_name", "collaboration_code", "recipient_email",
];

const RECIPIENT_OPTS = [
  { value: "", label: "(unspecified)" },
  { value: "customer", label: "customer" },
  { value: "attorney", label: "attorney" },
];

export default function EmailTemplatesSection() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/email-templates")
      .then(r => setRows(r.data))
      .catch(e => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Group by doc_type — the active row is the org override if any, else system default.
  const grouped = useMemo(() => {
    const map = {};
    for (const t of rows) {
      const k = t.doc_type;
      if (!map[k]) map[k] = { doc_type: k, system: null, override: null };
      if (t.is_system_default) map[k].system = t;
      else map[k].override = t;
    }
    return Object.values(map).sort((a, b) => a.doc_type.localeCompare(b.doc_type));
  }, [rows]);

  const onDelete = async (override) => {
    if (!confirm(`Revert "${override.name}" to system default?`)) return;
    setErr(""); setMsg("");
    try {
      await api.delete(`/email-templates/${override.id}`);
      setMsg("Reverted to system default.");
      load();
    } catch (e) { setErr(formatErr(e)); }
  };

  return (
    <div className="surface p-6 mb-6" data-testid="email-templates-section">
      <div className="flex items-center gap-2 mb-1">
        <EnvelopeSimple size={18} />
        <h2 className="text-lg font-medium">Email Templates</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Customise the email sent when a user shares a document from the Job Dashboard.
        Each <code className="kbd">doc_type</code> falls back to the built-in system default unless you save an override.
      </p>

      {err && <div className="text-xs text-destructive mb-2" data-testid="email-templates-error">{err}</div>}
      {msg && <div className="text-xs text-green-600 mb-2" data-testid="email-templates-msg">{msg}</div>}

      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-2">
          {grouped.map(g => {
            const active = g.override || g.system;
            if (!active) return null;
            return (
              <div
                key={g.doc_type}
                className="flex items-center gap-3 p-3 border border-border rounded hover:bg-muted/30"
                data-testid={`email-template-row-${g.doc_type}`}
              >
                <span className="inline-flex items-center px-2 py-0.5 rounded bg-primary/10 text-primary font-medium text-[11px] uppercase mono">
                  {g.doc_type}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{active.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{active.subject}</div>
                  <div className="text-[10px] mono text-muted-foreground">
                    {g.override ? (
                      <span className="text-amber-600">org override active</span>
                    ) : (
                      <span>system default</span>
                    )}
                    {active.default_recipient && <span> · default recipient: {active.default_recipient}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setEditing(g)}
                  data-testid={`email-template-edit-${g.doc_type}`}
                >
                  <PencilSimple size={12} /> {g.override ? "EDIT OVERRIDE" : "CREATE OVERRIDE"}
                </button>
                {g.override && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => onDelete(g.override)}
                    title="Delete override — revert to system default"
                    data-testid={`email-template-revert-${g.doc_type}`}
                  >
                    <ArrowCounterClockwise size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <EmailTemplateEditor
        group={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); setMsg("Saved override."); load(); }}
      />
    </div>
  );
}


function EmailTemplateEditor({ group, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!group) { setForm(null); return; }
    const base = group.override || group.system;
    setForm({
      doc_type: group.doc_type,
      name: group.override?.name || (base?.name ? `${base.name} (org)` : ""),
      subject: base?.subject || "",
      body: base?.body || "",
      default_recipient: base?.default_recipient || "",
    });
    setErr("");
  }, [group]);

  const save = async () => {
    if (!form) return;
    setSaving(true); setErr("");
    try {
      await api.post("/email-templates", {
        doc_type: form.doc_type,
        name: form.name.trim() || `${form.doc_type} template`,
        subject: form.subject,
        body: form.body,
        default_recipient: form.default_recipient || null,
      });
      if (onSaved) onSaved();
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const insertVar = (key) => {
    setForm(f => ({ ...f, body: (f.body || "") + `{{${key}}}` }));
  };

  return (
    <Drawer
      open={!!group}
      onClose={onClose}
      title={group ? `Email Template: ${group.doc_type}` : "Email Template"}
      testid="email-template-editor"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving} data-testid="email-template-cancel">CANCEL</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !form?.subject?.trim() || !form?.body?.trim()} data-testid="email-template-save">
            {saving ? "SAVING…" : "SAVE OVERRIDE"}
          </button>
        </>
      }
    >
      {!form ? null : (
        <div className="space-y-3" data-testid="email-template-form">
          <div className="surface p-2 text-[11px] text-muted-foreground">
            Saving creates a per-org override for <code className="kbd">{form.doc_type}</code>.
            Click <em>Revert</em> on the row to delete the override and fall back to the system default.
          </div>

          <div>
            <div className="label-xs mb-1">TEMPLATE NAME</div>
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="email-template-name"
            />
          </div>

          <div>
            <div className="label-xs mb-1">DEFAULT RECIPIENT</div>
            <select
              className="input"
              value={form.default_recipient}
              onChange={(e) => setForm({ ...form, default_recipient: e.target.value })}
              data-testid="email-template-recipient"
            >
              {RECIPIENT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <div className="label-xs mb-1">SUBJECT</div>
            <input
              className="input"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="email-template-subject"
            />
          </div>

          <div>
            <div className="label-xs mb-1">BODY</div>
            <textarea
              className="input"
              rows={14}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="email-template-body"
            />
          </div>

          <div>
            <div className="label-xs mb-1">AVAILABLE VARIABLES (CLICK TO INSERT)</div>
            <div className="flex flex-wrap gap-1" data-testid="email-template-vars">
              {KNOWN_VARS.map(v => (
                <button
                  key={v}
                  type="button"
                  className="btn btn-ghost btn-sm mono text-[10px]"
                  onClick={() => insertVar(v)}
                  data-testid={`email-template-var-${v}`}
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>

          {err && <div className="text-xs text-destructive" data-testid="email-template-editor-error">{err}</div>}
        </div>
      )}
    </Drawer>
  );
}
